import { configAt, withConfigAt } from '@vagusx/agent-core/v2/config-path';
import { FlaskConical, History, Plus, Share2, Trash2, TriangleAlert, X, Zap } from 'lucide-react';
import type {
  AgentComponent,
  AgentComponentCatalog,
  ComponentCallDirection,
  ComponentField,
  LineContent,
  WorkflowGraph,
  WorkflowNode,
  WorkflowProblem,
  WorkflowStep,
} from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { FieldEditor } from './FieldEditors';
import { LineEditor, type LineWords } from './LineEditor';
import { NextSteps, type ArrowSlot } from './NextSteps';
import {
  allExits,
  factLabel,
  lineWords,
  problemField,
  stepExits,
  visibleFields,
  withLineWords,
} from './graph';

/**
 * The selected step: its words, its settings, and what happens next.
 *
 * TWO SCOPES, SAID IN THE LAYOUT. Words and settings belong to the STEP, so
 * a shared step's edits reach every workflow that uses it (each on its next
 * publish). Where the call goes after it belongs to THIS workflow — the same
 * shared step can lead different places in two workflows — so "What happens
 * next" is a separate band below, saved on the workflow (ADR-276).
 */

export interface StepPatch {
  name?: string;
  config?: Record<string, unknown>;
  content?: LineContent;
}

interface StepSettingsProps {
  node: WorkflowNode;
  step: WorkflowStep;
  component: AgentComponent | undefined;
  catalog: AgentComponentCatalog;
  direction: ComponentCallDirection;
  factsBefore: readonly string[];
  problems: readonly WorkflowProblem[];
  ops: boolean;
  busy: boolean;
  usedElsewhere: boolean;
  graph: WorkflowGraph;
  steps: ReadonlyMap<string, WorkflowStep>;
  onSaveStep: (patch: StepPatch) => void;
  onSaveGraph: (graph: WorkflowGraph) => void;
  /** Open "Add a step" for one of this step's arrows. */
  onNewStep: (slot: ArrowSlot) => void;
  /** Take out the conditions this step carries from before arrows could branch. */
  onClearGates: () => void;
  /** Take the step out of this workflow; absent for the first step. */
  onRemove?: () => void;
  /** Start a test call on just this step. */
  onTest: () => void;
}

export function StepSettings({
  node,
  step,
  component,
  catalog,
  direction,
  factsBefore,
  problems,
  ops,
  busy,
  usedElsewhere,
  graph,
  steps,
  onSaveStep,
  onSaveGraph,
  onNewStep,
  onClearGates,
  onRemove,
  onTest,
}: StepSettingsProps): React.ReactElement {
  if (!component) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        This step uses “{step.componentKey}”, which this version of VagusX does not offer. Remove
        it, or ask VagusX.
      </p>
    );
  }
  const { meta } = component;
  const fields = visibleFields(meta.ui, step.config, ops);
  const fieldMessages = (path: string, warning: boolean): string | undefined =>
    problems
      .filter((p) => problemField(p) === path && (p.severity === 'warning') === warning)
      .map((p) => p.message)
      .join(' ') || undefined;
  const fieldProblems = (path: string): string | undefined => fieldMessages(path, false);
  const fieldWarnings = (path: string): string | undefined => fieldMessages(path, true);
  const isGate = (p: WorkflowProblem): boolean =>
    Boolean(p.at?.startsWith('requires') || p.at?.startsWith('unless'));
  const isArrow = (p: WorkflowProblem): boolean => Boolean(p.at?.startsWith('transitions'));
  const unplaced = problems.filter((p) => {
    const field = problemField(p);
    if (field && fields.some((f) => f.path === field)) return false;
    return !isGate(p) && !isArrow(p);
  });
  const gateProblems = problems.filter(isGate);
  const arrowProblems = problems.filter(isArrow);
  const exits = stepExits(step, component, catalog.decisionPresets);
  const sharedExits = meta.outcomes.filter((o) => o.shared && o.shared !== 'end_call');

  // A field's path is a DOT path into the config (`frontDesk.topics`).
  const saveConfig = (path: string, value: unknown): void => {
    onSaveStep({ config: withConfigAt(step.config, path, value) });
  };

  const renderField = (field: ComponentField): React.ReactNode => {
    if (field.kind === 'locale-text' && field.storage === 'slot') {
      return (
        <LineEditor
          words={lineWords(step.content, step.config, field.path, field.defaultSlot)}
          facts={catalog.callFacts}
          factsNote={catalog.callFactsNote}
          direction={direction}
          vars={field.vars}
          callFacts={field.callFacts}
          disabled={busy}
          error={fieldProblems(field.path)}
          warning={fieldWarnings(field.path)}
          onCommit={(words) =>
            onSaveStep(withLineWords(step, field.path, words, field.defaultSlot))
          }
        />
      );
    }
    if (field.kind === 'locale-text') {
      const value = configAt(step.config, field.path) as Partial<LineWords> | undefined;
      return (
        <LineEditor
          words={{ en: value?.en ?? '', es: value?.es ?? '' }}
          facts={catalog.callFacts}
          factsNote={catalog.callFactsNote}
          direction={direction}
          vars={field.vars}
          callFacts={field.callFacts}
          disabled={busy}
          error={fieldProblems(field.path)}
          warning={fieldWarnings(field.path)}
          spanishFallback="Left empty, the agent asks Spanish callers in its own words"
          onCommit={(words) =>
            saveConfig(
              field.path,
              words.en.trim() || words.es.trim()
                ? { en: words.en, ...(words.es.trim() ? { es: words.es } : {}) }
                : undefined
            )
          }
        />
      );
    }
    if (field.kind === 'custom' && field.editor === 'line-list') {
      return (
        <LineList
          path={field.path}
          step={step}
          catalog={catalog}
          direction={direction}
          disabled={busy}
          error={fieldProblems(field.path)}
          onSave={onSaveStep}
        />
      );
    }
    return (
      <div className="space-y-1">
        <FieldEditor
          field={field}
          value={configAt(step.config, field.path)}
          options={catalog.options}
          facts={factsBefore}
          disabled={busy}
          onCommit={(v) => saveConfig(field.path, v)}
        />
        {fieldProblems(field.path) && (
          <p className="text-xs text-destructive">{fieldProblems(field.path)}</p>
        )}
        {fieldWarnings(field.path) && (
          <p className="text-xs text-muted-foreground">{fieldWarnings(field.path)}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-medium">{meta.title}</h3>
          <p className="text-sm text-muted-foreground">{meta.summary}</p>
        </div>
        <Button variant="outline" size="sm" disabled={busy} onClick={onTest}>
          <FlaskConical className="mr-2 h-3.5 w-3.5" />
          Test this step
        </Button>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove this step"
            title="Remove this step. The call carries on to where it went."
            disabled={busy}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {step.ownerWorkflowId === null && (
        <div className="flex gap-2 rounded-md border px-3 py-2 text-xs text-muted-foreground">
          <Share2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            A shared step.{' '}
            {usedElsewhere
              ? 'Its words and settings change in every workflow that uses it, each time that workflow is next published.'
              : 'Other workflows can use it too.'}
          </span>
        </div>
      )}

      {unplaced.length > 0 && (
        <ul className="space-y-1">
          {unplaced.map((p, i) => (
            <li
              key={i}
              className={
                p.severity === 'warning'
                  ? 'flex gap-2 text-xs text-muted-foreground'
                  : 'flex gap-2 text-xs text-destructive'
              }
            >
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {p.message}
            </li>
          ))}
        </ul>
      )}

      <section className="space-y-4">
        {fields.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing to change on this step.</p>
        )}
        {fields.map((field) => (
          <div key={field.path} className="space-y-1.5">
            <div className="text-sm font-medium">
              {field.label}
              {field.ops && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">VagusX only</span>
              )}
            </div>
            {field.help && <p className="text-xs text-muted-foreground">{field.help}</p>}
            {renderField(field)}
          </div>
        ))}
      </section>

      <section className="space-y-3 border-t pt-4">
        <div>
          <h4 className="text-sm font-medium">What happens next</h4>
          <p className="text-xs text-muted-foreground">In this workflow.</p>
        </div>
        <NextSteps
          graph={graph}
          node={node}
          exits={exits}
          labels={allExits(step, component, catalog.decisionPresets)}
          steps={steps}
          busy={busy}
          onChange={onSaveGraph}
          onNewStep={onNewStep}
        />
        {arrowProblems.map((p, i) => (
          <p key={i} className="text-xs text-destructive">
            {p.message}
          </p>
        ))}
        {sharedExits.length > 0 && (
          <p className="flex gap-1.5 text-xs text-muted-foreground">
            <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            If the caller asks for{' '}
            {sharedExits
              .map((o) => (o.shared === 'handoff' ? 'a person' : 'a callback'))
              .join(' or ')}{' '}
            during this step, the workflow’s “At any point in the call” rules decide where they go.
          </p>
        )}
      </section>

      {(node.requires?.length || node.unless?.length) && (
        <section className="space-y-2 border-t pt-4">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-medium">Older conditions</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            This step was set up before arrows could branch, so it also has conditions of its own.
            They still apply on every call. Remove them once the arrows above say the same thing.
          </p>
          <ul className="space-y-1 text-xs">
            {node.requires?.length ? (
              <li>Runs only once: {node.requires.map(factLabel).join(', ')}</li>
            ) : null}
            {node.unless?.length ? (
              <li>Skipped if: {node.unless.map(factLabel).join(' or ')}</li>
            ) : null}
          </ul>
          {gateProblems.map((p, i) => (
            <p key={i} className="text-xs text-destructive">
              {p.message}
            </p>
          ))}
          <Button variant="outline" size="sm" disabled={busy} onClick={onClearGates}>
            Remove these conditions
          </Button>
        </section>
      )}
    </div>
  );
}

/** An ordered list of lines, each with its own words: `Reminders`' lines. */
function LineList({
  path,
  step,
  catalog,
  direction,
  disabled,
  error,
  onSave,
}: {
  path: string;
  step: WorkflowStep;
  catalog: AgentComponentCatalog;
  direction: ComponentCallDirection;
  disabled: boolean;
  error?: string;
  onSave: (patch: StepPatch) => void;
}): React.ReactElement {
  const listed = configAt(step.config, path);
  const names = Array.isArray(listed)
    ? listed.filter((n): n is string => typeof n === 'string')
    : [];
  const freshName = (): string => {
    const used = new Set([...names, ...Object.keys(step.content)]);
    for (let i = names.length + 1; ; i++) if (!used.has(`${path}_${i}`)) return `${path}_${i}`;
  };
  const setWords = (name: string, words: LineWords): void => {
    const content = { ...step.content };
    content[name] = {
      ...(words.en.trim() ? { en: words.en } : {}),
      ...(words.es.trim() ? { es: words.es } : {}),
    };
    onSave({ content });
  };
  const remove = (name: string): void => {
    const content = { ...step.content };
    delete content[name];
    const rest = names.filter((n) => n !== name);
    const config = withConfigAt(step.config, path, rest.length ? rest : undefined);
    onSave({ config, content });
  };
  return (
    <div className="space-y-3">
      {names.map((name, i) => (
        <div key={name} className="space-y-1 rounded-md border p-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            Line {i + 1}
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Remove line ${i + 1}`}
              disabled={disabled}
              onClick={() => remove(name)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <LineEditor
            words={{ en: step.content[name]?.en ?? '', es: step.content[name]?.es ?? '' }}
            facts={catalog.callFacts}
            factsNote={catalog.callFactsNote}
            direction={direction}
            disabled={disabled}
            onCommit={(words) => setWords(name, words)}
          />
        </div>
      ))}
      {error && <p className="text-xs text-destructive">{error}</p>}
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onSave({ config: { ...step.config, [path]: [...names, freshName()] } })}
      >
        <Plus className="mr-2 h-3.5 w-3.5" />
        Add a line
      </Button>
    </div>
  );
}
